import { FAMILIAS_DE_MATERIAL, ROTULO_DA_FAMILIA, ROTULO_DO_TIPO_DE_EQUIPAMENTO, type ItemDaBaseEntrada } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { SeletorDeChips } from "../SeletorDeChips";
import { Campo, CampoPerfis, SeletorDeQuimica, TextoComSugestoes, entradaCls, opcoesDeMaterial, opcoesDeQuimica, type OpcoesDaBase } from "./comum";

type Props<T> = { item: T; mudar: (parcial: Partial<T>) => void; opcoes: OpcoesDaBase };

type Do<K extends ItemDaBaseEntrada["tipo"]> = Extract<ItemDaBaseEntrada, { tipo: K }>;

function Nome({ valor, mudar }: { valor: string; mudar: (nome: string) => void }) {
  return (
    <Campo rotulo="Nome">
      <input value={valor} onChange={(e) => mudar(e.target.value)} className={entradaCls} />
    </Campo>
  );
}

export function CamposServico({ item, mudar, opcoes }: Props<Do<"servicos">>) {
  return (
    <>
      <Nome valor={item.nome} mudar={(nome) => mudar({ nome })} />
      <Campo rotulo="Categoria" dica="Escolha uma existente ou digite uma nova.">
        <TextoComSugestoes id="categorias" valor={item.categoria} onChange={(categoria) => mudar({ categoria })} sugestoes={opcoes.categorias.map((c) => c.nome)} />
      </Campo>
      <Campo rotulo="Cobrança">
        <div className="inline-flex rounded-md border border-admin-200 p-0.5">
          {(["DENTE", "ARCADA"] as const).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => mudar({ abrangencia: a })}
              aria-pressed={item.abrangencia === a}
              className={cn("rounded px-3 py-1 text-sm", item.abrangencia === a ? "bg-admin-900 text-white" : "text-admin-700 hover:bg-admin-50")}
            >
              {a === "DENTE" ? "Por dente" : "Por arcada"}
            </button>
          ))}
        </div>
      </Campo>
      <Campo rotulo="Materiais compatíveis" dica="O que o dentista pode escolher neste trabalho. Sem material = trabalho só digital.">
        <SeletorDeChips opcoes={opcoesDeMaterial(opcoes)} valor={item.materialIds ?? []} onChange={(materialIds) => mudar({ materialIds })} />
      </Campo>
      <CampoPerfis valor={item.perfis ?? []} onChange={(perfis) => mudar({ perfis })} />
    </>
  );
}

export function CamposMaterial({ item, mudar, opcoes }: Props<Do<"materiais">>) {
  return (
    <>
      <Nome valor={item.nome} mudar={(nome) => mudar({ nome })} />
      <Campo rotulo="Família" dica="Agrupa o material no onboarding do laboratório.">
        <select value={item.familia ?? ""} onChange={(e) => mudar({ familia: e.target.value || null })} className={entradaCls}>
          <option value="">Sem família</option>
          {FAMILIAS_DE_MATERIAL.map((f) => (
            <option key={f.codigo} value={f.codigo}>
              {f.rotulo}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Química" dica="Classifica o material (insumos, equipamentos). Em que trabalho ele vale se marca no próprio trabalho.">
        <SeletorDeQuimica opcoes={opcoes} valor={item.materialCategoryId} onChange={(materialCategoryId) => mudar({ materialCategoryId })} />
      </Campo>
      <CampoPerfis valor={item.perfis ?? []} onChange={(perfis) => mudar({ perfis })} />
    </>
  );
}

export function CamposQuimica({ item, mudar }: Props<Do<"quimicas">>) {
  return (
    <>
      <Nome valor={item.nome} mudar={(nome) => mudar({ nome })} />
      <Campo rotulo="Família">
        <select value={item.familia ?? ""} onChange={(e) => mudar({ familia: e.target.value || null })} className={entradaCls}>
          <option value="">Sem família</option>
          {Object.entries(ROTULO_DA_FAMILIA).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </Campo>
    </>
  );
}

export function CamposCor({ item, mudar, opcoes }: Props<Do<"cores">>) {
  return (
    <>
      <Campo rotulo="Escala">
        <select value={item.escalaId} onChange={(e) => mudar({ escalaId: e.target.value })} className={entradaCls}>
          <option value="">Escolha a escala</option>
          {opcoes.escalas.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nome}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Cor" dica='O código como aparece na escala: "A2", "2M2", "BL1".'>
        <input value={item.cor} onChange={(e) => mudar({ cor: e.target.value })} className={entradaCls} />
      </Campo>
    </>
  );
}

export function CamposEquipamento({ item, mudar, opcoes }: Props<Do<"equipamentos">>) {
  return (
    <>
      <Nome valor={item.nome} mudar={(nome) => mudar({ nome })} />
      <Campo rotulo="Tipo">
        <select value={item.tipoDeEquipamento} onChange={(e) => mudar({ tipoDeEquipamento: e.target.value })} className={entradaCls}>
          <option value="">Escolha o tipo</option>
          {Object.entries(ROTULO_DO_TIPO_DE_EQUIPAMENTO).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </Campo>
      <Campo rotulo="Químicas com que trabalha" dica="Liga a máquina aos materiais do laboratório da mesma química.">
        <SeletorDeChips opcoes={opcoesDeQuimica(opcoes)} valor={item.quimicaIds ?? []} onChange={(quimicaIds) => mudar({ quimicaIds })} />
      </Campo>
      <CampoPerfis valor={item.perfis ?? []} onChange={(perfis) => mudar({ perfis })} />
    </>
  );
}
